import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';

interface CataloguePageProps {
  t: any;
  isRTL: boolean;
}

interface DatabaseProduct {
  id: string;
  name: string;
  mainImage?: string;
  category: string;
  createdAt?: any;
  [key: string]: any;
}

const CataloguePageContainer = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeaderSection = styled.div`
  margin-bottom: 3rem;
`;

const PageTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CategorySection = styled(motion.div)`
  margin-bottom: 4rem;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const CategoryTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ViewAllLink = styled.a<{ $isRTL: boolean }>`
  color: #dc2626;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  transition: color 0.3s ease;
  cursor: pointer;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:hover {
    color: #b91c1c;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f1f5f9;
  position: relative;
  height: 320px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
    border-color: #e2e8f0;

    &::before {
      opacity: 1;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    height: 280px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const ProductImage = styled.div`
  height: 200px;
  width: 100%;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    border-radius: 8px;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
  }
  
  ${ProductCard}:hover & img {
    transform: scale(1.05);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${ProductCard}:hover &::after {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    height: 180px;
    padding: 0.75rem;
  }
`;

const ProductContent = styled.div<{ $isRTL: boolean }>`
  padding: 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ProductLabel = styled.h3<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ProductBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #dc2626, #ef4444);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  z-index: 2;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const LoadingText = styled.p`
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const EmptyStateText = styled.p`
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
`;

const CataloguePage: React.FC<CataloguePageProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [groupesElectrogenesProducts, setGroupesElectrogenesProducts] = useState<DatabaseProduct[]>([]);
  const [loadingGroupesElectrogenes, setLoadingGroupesElectrogenes] = useState(true);
  const [materielElectriqueSubcategories, setMaterielElectriqueSubcategories] = useState<any[]>([]);
  const [loadingMaterielElectrique, setLoadingMaterielElectrique] = useState(true);
  const [eclairagePublicSubcategories, setEclairagePublicSubcategories] = useState<any[]>([]);
  const [loadingEclairagePublic, setLoadingEclairagePublic] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load Groupes Électrogènes products from database (oldest to newest)
  useEffect(() => {
    const loadGroupesElectrogenesProducts = async () => {
      try {
        setLoadingGroupesElectrogenes(true);
        const allProducts = await FirebaseService.getAll('products');
        
        // Filter for Groupes Électrogènes (category '1') and sort by creation date (newest first)
        const groupesProducts = (allProducts || [])
          .filter((product: DatabaseProduct) => product.category === '1')
          .sort((a: DatabaseProduct, b: DatabaseProduct) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
          })
          .slice(0, 4); // Take first 4 products
        
        setGroupesElectrogenesProducts(groupesProducts);
      } catch (error) {
        console.error('Error loading Groupes Électrogènes products:', error);
        setGroupesElectrogenesProducts([]);
      } finally {
        setLoadingGroupesElectrogenes(false);
      }
    };

    loadGroupesElectrogenesProducts();
  }, []);

  // Define predefined Matériel Électrique subcategories
  useEffect(() => {
    const predefinedSubcategories = [
      {
        id: 'solutions',
        name: 'Solutions',
        title: 'Solutions',
        mainImage: '/images/materielimg/solutions.png',
        category: 'materiel-electrique',
        subcategory: 'solutions',
        description: 'Solutions complètes pour vos projets électriques'
      },
      {
        id: 'isolateurs-composites',
        name: 'Isolateurs Composites HTA / HTB',
        title: 'Isolateurs Composites HTA / HTB',
        mainImage: '/images/materielimg/isolateurs.png',
        category: 'materiel-electrique',
        subcategory: 'isolateurs-composites',
        description: 'Isolateurs haute performance pour réseaux électriques'
      },
      {
        id: 'accessoires-chaines',
        name: 'Accessoires pour Chaînes d\'Isolateurs',
        title: 'Accessoires pour Chaînes d\'Isolateurs',
        mainImage: '/images/materielimg/accessoires pour chaines.png',
        category: 'materiel-electrique',
        subcategory: 'accessoires-chaines',
        description: 'Accessoires et composants pour chaînes d\'isolateurs'
      },
      {
        id: 'interrupteur-aerien',
        name: 'Interrupteur Aérien à Commande Manuelle IACM',
        title: 'Interrupteur Aérien à Commande Manuelle IACM',
        mainImage: '/images/materielimg/interrupteurs aeriens.png',
        category: 'materiel-electrique',
        subcategory: 'interrupteur-aerien',
        description: 'Interrupteurs aériens pour réseaux de distribution'
      }
    ];

    setMaterielElectriqueSubcategories(predefinedSubcategories);
        setLoadingMaterielElectrique(false);
  }, []);

  // Load Éclairage Public subcategories (hardcoded with actual images)
  useEffect(() => {
    const loadEclairagePublicSubcategories = () => {
      setLoadingEclairagePublic(true);
      
      // Hardcoded subcategories with actual images from gammes folder
      const subcategories = [
        {
          id: 'GOeuM9xgI3DbRdhl6Sg9',
          name: t.functionalLightingTitle,
          mainImage: '/images/catalog/Eclairage/gammes/1.png',
          title: t.functionalLightingTitle
        },
        {
          id: 'K7qdZzSibKRTfNMnq8n5',
          name: t.decorativeLightingTitle,
          mainImage: '/images/catalog/Eclairage/gammes/3.png',
          title: t.decorativeLightingTitle
        },
        {
          id: 'Mz00QWkWlGPKjyotTqA0',
          name: t.signageSupportsTitle,
          mainImage: '/images/catalog/Eclairage/gammes/4.png',
          title: t.signageSupportsTitle
        },
        {
          id: 'yDLRQsrzDKG8ASG53Kdw',
          name: t.sportsLightingTitle,
          mainImage: '/images/catalog/Eclairage/gammes/27.png',
          title: t.sportsLightingTitle
        }
      ];
      
      setEclairagePublicSubcategories(subcategories);
      setLoadingEclairagePublic(false);
    };

    loadEclairagePublicSubcategories();
  }, []);

  const categories = [
    {
      id: 'cables-transport',
      title: t.cableTransportTitle,
      products: [
        {
          id: 'lignes-aeriennes',
          name: t.overheadLinesTitle,
          image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Cuivre nu écroui.jpg'
        },
        {
          id: 'cables-mt',
          name: t.mediumVoltageCablesTitle,
          image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Cable MT NFC 33-226 1220 (24) kV.jpg'
        },
        {
          id: 'cables-bt',
          name: t.lowVoltageCablesTitle,
          image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Torsadé de branchement.jpg'
        },
        {
          id: 'conducteurs-aaac',
          name: t.aaacConductorsTitle,
          image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Conducteurs en alliage d\'aluminium (AAAC).jpg'
        }
      ]
    },
    {
      id: 'cables-batiments',
      title: t.cableBuildingsTitle,
      products: [
        {
          id: 'fils-cables-1kv',
          name: t.standardCables1kvTitle,
          image: '/images/catalog/Câbles de bâtiments/U-1000 R2V (industriel non armé).jpg'
        },
        {
          id: 'fils-cables-1kv-inf',
          name: t.standardCablesLt1kvTitle,
          image: '/images/catalog/Câbles de bâtiments/0.61K RV-K.jpg'
        },
        {
          id: 'cables-speciaux',
          name: t.specialCablesTitle,
          image: '/images/catalog/Câbles de bâtiments/H05VV-F (U500 SV1V).jpg'
        },
        {
          id: 'cables-armes',
          name: t.armouredCablesTitle,
          image: '/images/catalog/Câbles de bâtiments/U-1000 AR2V (industriels non armés).jpg'
        }
      ]
    },
    {
      id: 'transformateurs',
      title: t.transformersTitle,
      products: [
        {
          id: 'novare-residentiel',
          name: 'NOVARE Résidentiel',
          image: '/images/catalog/transformateurs/NOVARE Résidentiel.jpg'
        },
        {
          id: 'novare-poteau',
          name: 'NOVARE Poteau',
          image: '/images/catalog/transformateurs/NOVARE Poteau.jpg'
        },
        {
          id: 'novare-cabine',
          name: 'NOVARE Cabine',
          image: '/images/catalog/transformateurs/NOVARE Cabine.jpg'
        },
        {
          id: 'novare-power',
          name: 'NOVARE Power',
          image: '/images/catalog/transformateurs/NOVARE Power.jpg'
        }
      ]
    },
    {
      id: 'cellules-hta',
      title: t.switchgearTitle,
      products: [
        {
          id: 'cellules-fluocell',
          name: t.fluocellSwitchgearTitle,
          image: '/images/catalog/Cellule HTA.jpg'
        }
      ]
    },
    {
      id: 'postes-prefabriques',
      title: t.prefabricatedSubstationsTitle,
      products: [
        {
          id: 'clipper-c32-c37',
          name: 'CLIPPER C32 - CLIPPER C37',
          image: '/images/catalog/postes/CLIPPER C32 - CLIPPER C37/clipper1.jpg'
        },
        {
          id: 'alto-a',
          name: 'ALTO A',
          image: '/images/catalog/postes/ALTO A.jpg'
        },
        {
          id: 'bocage',
          name: 'BOCAGE',
          image: '/images/catalog/postes/BOCAGE.jpg'
        }
      ]
    },
    {
      id: 'groupes-electrogenes',
      title: t.generatorsTitle,
      products: [], // Will be populated from database
      isDatabase: true
    },
    {
      id: 'materiel-electrique',
      title: t.electricalEquipmentTitle,
      products: [], // Will be populated from database
      isDatabase: true,
      isSubcategories: true
    },
    {
      id: 'eclairage-public',
      title: t.publicLightingTitle,
      products: [], // Will be populated from database
      isDatabase: true,
      isSubcategories: true
    }
  ];

  const handleProductClick = async (productId: string, categoryId: string) => {
    // Special case for Solutions subcategory - navigate to hardcoded Solutions page
    if (productId === 'solutions' && categoryId === 'materiel-electrique') {
      navigate('/solutions');
      return;
    }
    
    // Navigate to specific product detail page if it exists
    if (productId === 'clipper-c32-c37') {
      navigate('/product/clipper-c32-c37');
    } else if (productId === 'alto-a') {
      navigate('/product/alto-a');
    } else if (productId === 'bocage') {
      navigate('/product/bocage');
    } else if (productId === 'cellules-fluocell') {
      navigate('/product/cellules-fluocell');
    } else if (productId === 'novare-poteau') {
      navigate('/product/novare-poteau');
    } else if (productId === 'novare-cabine') {
      navigate('/product/novare-cabine');
    } else if (productId === 'novare-power') {
      navigate('/product/novare-power');
    } else if (categoryId === 'groupes-electrogenes') {
      // Navigate to groupes electrogenes detail page with the specific product ID
      navigate(`/product/${productId}`);
    } else if (categoryId === 'eclairage-public') {
      // Navigate to specific gamme subcategory page
      navigate(`/category/eclairage-public/subcategory/${productId}`);
    } else if (categoryId === 'materiel-electrique') {
      // Navigate to materiel electrique subcategory page
      navigate(`/category/materiel-electrique/${productId}`);
    } else {
      // Navigate to category page for other products
      navigate(`/category/${categoryId}`);
    }
  };

  const handleViewAllClick = (categoryId: string) => {
    if (categoryId === 'groupes-electrogenes') {
      navigate('/category/groupes-electrogenes'); // Navigate to Groupes Électrogènes category page
    } else if (categoryId === 'eclairage-public') {
      // Navigate to eclairage public category page
      navigate('/category/eclairage-public');
    } else if (categoryId === 'eclairage-public') {
      navigate('/category/eclairage-public'); // Navigate to Éclairage Public category page
    } else if (categoryId === 'materiel-electrique') {
      navigate('/category/materiel-electrique'); // Navigate to Matériel Électrique category page
    } else {
      navigate(`/category/${categoryId}`);
    }
  };

  const renderProducts = (category: any) => {
    if (category.isDatabase) {
      // Render database products/subcategories for database categories
      if (category.id === 'groupes-electrogenes' && loadingGroupesElectrogenes) {
        return (
          <LoadingContainer>
            <LoadingText>{t.loadingProducts}</LoadingText>
          </LoadingContainer>
        );
      }
      
      if (category.id === 'materiel-electrique' && loadingMaterielElectrique) {
        return (
          <LoadingContainer>
            <LoadingText>{t.loadingSubcategories}</LoadingText>
          </LoadingContainer>
        );
      }

      if (category.id === 'groupes-electrogenes' && groupesElectrogenesProducts.length === 0) {
        return (
          <EmptyStateContainer>
            <EmptyStateIcon>🔧</EmptyStateIcon>
            <EmptyStateText>Aucun produit disponible</EmptyStateText>
          </EmptyStateContainer>
        );
      }
      
      if (category.id === 'materiel-electrique' && materielElectriqueSubcategories.length === 0) {
        return (
          <EmptyStateContainer>
            <EmptyStateIcon>⚡</EmptyStateIcon>
            <EmptyStateText>Aucune sous-catégorie disponible</EmptyStateText>
          </EmptyStateContainer>
        );
      }

      const itemsToRender = category.id === 'groupes-electrogenes' ? groupesElectrogenesProducts : category.id === 'materiel-electrique' ? materielElectriqueSubcategories : eclairagePublicSubcategories;
      
      return itemsToRender.map((item, itemIndex) => (
        <ProductCard
          key={item.id}
          $isRTL={isRTL}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.4, 
            delay: itemIndex * 0.1 
          }}
          onClick={() => handleProductClick(item.id, category.id)}
        >
          <ProductImage>
            <img
              src={item.mainImage || item.images?.[0] || '/images/products/default-product.jpg'}
              alt={item.name || item.title || t.product}
              loading="lazy"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/images/products/default-product.jpg';
              }}
            />
          </ProductImage>
          <ProductContent $isRTL={isRTL}>
            <ProductLabel $isRTL={isRTL}>
              {item.name || item.title || t.product}
            </ProductLabel>
          </ProductContent>
        </ProductCard>
      ));
    } else {
      // Render hardcoded products for other categories
      return category.products.map((product: any, productIndex: number) => (
        <ProductCard
          key={product.id}
          $isRTL={isRTL}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.4, 
            delay: productIndex * 0.1 
          }}
          onClick={() => handleProductClick(product.id, category.id)}
        >
          <ProductImage>
              <img
                src={product.image}
                alt={product.name}
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/products/default-product.jpg';
                }}
              />
          </ProductImage>
          <ProductContent $isRTL={isRTL}>
            <ProductLabel $isRTL={isRTL}>
              {product.name}
            </ProductLabel>
          </ProductContent>
        </ProductCard>
      ));
    }
  };

  return (
    <CataloguePageContainer $isRTL={isRTL}>
      <Container>
        <HeaderSection>
          <PageTitle $isRTL={isRTL}>{t.catalogTitle}</PageTitle>
        </HeaderSection>

        {categories.map((category, categoryIndex) => (
          <CategorySection 
            key={category.id} 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.6, 
              delay: categoryIndex * 0.1 
            }}
          >
            <CategoryHeader>
              <CategoryTitle $isRTL={isRTL}>{category.title}</CategoryTitle>
              <ViewAllLink 
                $isRTL={isRTL}
                onClick={() => handleViewAllClick(category.id)}
              >
{t.viewAllProductsCatalog} {isRTL ? '←' : '→'}
              </ViewAllLink>
            </CategoryHeader>
            
            <ProductsGrid>
              {renderProducts(category)}
            </ProductsGrid>
          </CategorySection>
        ))}
      </Container>
    </CataloguePageContainer>
  );
};

export default CataloguePage;
